import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { FC, useState } from 'react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuCheckboxItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ArrowDownIcon from '@/components/icons/ArrowDownIcon';
import { useTranslation } from 'react-i18next';
import CancelIcon from '@/components/icons/CancelIcon';
import { SettlementsType } from '../../types/finances-dto';

type Props = {
    label?: string;
    value?: SettlementsType;
    onSelect?: (type?: SettlementsType) => void;
    containerClassName?: string;
    disabled?: boolean;
};

const SettlementsTypePicker: FC<Props> = ({
    label,
    value,
    onSelect,
    containerClassName,
    disabled = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const { t } = useTranslation();

    const settlementsTypes: { name: string; type?: SettlementsType }[] = [
        {
            name: 'main.finances.components.settlements.components.details.types.all',
            type: undefined,
        },
        {
            name: 'main.finances.components.settlements.components.details.types.property',
            type: SettlementsType.Property,
        },
        {
            name: 'main.finances.components.settlements.components.details.types.otherServices',
            type: SettlementsType.OtherServices,
        },
    ];

    const isOpenToggler = () => {
        setIsOpen((prev) => !prev);
    };

    const handleSelectType = (type?: SettlementsType) => {
        onSelect?.(type);
        setIsOpen((prev) => !prev);
    };

    const handleResetType = (e: React.MouseEvent) => {
        e.preventDefault();
        onSelect?.(undefined);
    };

    return (
        <div className={cn('flex flex-col gap-1.5 select-none', containerClassName)}>
            {label && <h4 className="text-sm font-medium">{label}</h4>}
            <DropdownMenu open={isOpen} onOpenChange={isOpenToggler} modal={true}>
                <DropdownMenuTrigger
                    asChild
                    className={cn(
                        'w-36 h-9 px-4 py-3 flex rounded-full text-popover-1000 hover:bg-popover-50 hover:shadow-none disabled:bg-popover-10 transition-all duration-300',
                        isOpen ? 'border-primary-color-1000' : '',
                        value && 'bg-popover-1000 text-popover-10 hover:bg-popover-1000',
                    )}
                >
                    <Button variant="outline" disabled={disabled}>
                        <span className="text-xs text-inherit truncate">
                            {t(`${settlementsTypes.find(({ type }) => type === value)?.name}`)}
                        </span>
                        {!value && (
                            <div
                                className={cn(
                                    'transition-transform duration-50 ease-in-out ml-auto',
                                    isOpen ? '-rotate-180' : 'rotate-0',
                                )}
                            >
                                {<ArrowDownIcon />}
                            </div>
                        )}
                        {value && (
                            <div className={cn('ml-auto')} onPointerDown={handleResetType}>
                                {<CancelIcon className={cn(value && 'stroke-popover-10')} />}
                            </div>
                        )}
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    className={
                        'w-(--radix-dropdown-menu-trigger-width) h-fit max-h-50 p-0 rounded-semi-xl border border-popover-100 bg-popover-10 overflow-y-scroll no-scrollbar'
                    }
                >
                    {settlementsTypes.map(({ name, type }, index) => (
                        <DropdownMenuCheckboxItem
                            key={index}
                            className="px-3.5 py-3"
                            checked={type === value}
                            onSelect={(e) => e.preventDefault()}
                            onCheckedChange={() => handleSelectType(type)}
                        >
                            {t(name)}
                        </DropdownMenuCheckboxItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
};

export default SettlementsTypePicker;
